import React from 'react';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
    pending:   { color: '#d97706', bg: '#fffbeb' },
    confirmed: { color: '#16a34a', bg: '#dcfce7' },
    cancelled: { color: '#ef4444', bg: '#fef2f2' },
    default:   { color: '#6b7280', bg: '#f3f4f6' },
};

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.default;
    return (
        <span style={{
            fontSize: 11, fontWeight: 700,
            color: s.color, backgroundColor: s.bg,
            borderRadius: 12, padding: '3px 9px',
            textTransform: 'capitalize',
        }}>
            {status?.replace(/_/g, ' ') || t('common.symbols.emDash')}
        </span>
    );
};

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted, bold }) => {
    const { t } = useTranslation();
    return (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: bold ? 700 : muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? t('common.symbols.emDash')}
    </td>
    );
};



const formatDate = (d, emDash) => {
    if (!d) return emDash;
    const clean = String(d).split('T')[0];
    try {
        return new Date(clean).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return clean;
    }
};

const SalesOrdersTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    const strings = t('salesAnalytics.ordersTable', { returnObjects: true });
    const emDash = t('common.symbols.emDash');
    return (
    <div style={cardStyle}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{strings.title}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{strings.subtitle}</div>
            </div>
            {items.length > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', borderRadius: 20, padding: '3px 10px' }}>
                    {strings.ordersCount.replace('{count}', items.length)}
                </span>
            )}
        </div>

        {loading && <EmptyState message={strings.loading} />}
        {!loading && !items.length && <EmptyState message={strings.noData} />}

        {!loading && items.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                    <thead>
                            <tr>
                                <Th>{strings.headers.index}</Th>
                                <Th>{strings.headers.orderNo}</Th>
                                <Th>{strings.headers.customer}</Th>
                                <Th>{strings.headers.salesStaff}</Th>
                                <Th>{strings.headers.status}</Th>
                                <Th>{strings.headers.date}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr
                                    key={row.order_number ?? row.id ?? i}
                                    style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                >
                                    <Cell muted>{i + 1}</Cell>
                                    <Cell>
                                        <span style={{ fontWeight: 700, color: '#16a34a' }}>
                                            {row.order_number ?? row.order_no ?? emDash}
                                        </span>
                                    </Cell>
                                    <Cell>{row.customer_name ?? row.customer ?? emDash}</Cell>
                                    <Cell muted>{row.sales_staff_name ?? row.staff_name ?? row.created_by ?? emDash}</Cell>
                                    <Cell><StatusBadge status={row.status} /></Cell>
                                    <Cell muted>{formatDate(row.order_date ?? row.created_at ?? row.date, emDash)}</Cell>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default SalesOrdersTable;
